import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storagePromise } from 'configuration';

export const imageService = {
    /**
     * Validates image file type and size
     */
    validateFile: (file, maxSizeMB = 5) => {
        if (!file.type.startsWith('image/')) {
            throw new Error('Please select a valid image file');
        }

        const maxSizeBytes = maxSizeMB * 1024 * 1024;
        if (file.size > maxSizeBytes) {
            throw new Error(`Image must be smaller than ${maxSizeMB}MB`);
        }

        return true;
    },

    /**
     * Creates image preview from file
     */
    createPreview: (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    },

    /**
     * Uploads single image to Firebase Storage
     */
    uploadSingle: async (file, restaurantId, type) => {
        const storage = await storagePromise;
        const imageRef = ref(storage, `restaurants/${restaurantId}/${type}_${Date.now()}`);
        const snapshot = await uploadBytes(imageRef, file);
        return await getDownloadURL(snapshot.ref);
    },

    /**
     * Uploads multiple images to Firebase Storage
     */
    uploadImages: async (restaurantId, logoFile, coverFile, setLogoUploading, setCoverUploading) => {
        const uploadPromises = [];

        if (logoFile) {
            setLogoUploading(true);
            const logoPromise = (async () => {
                try {
                    return await imageService.uploadSingle(logoFile, restaurantId, 'logo');
                } catch (error) {
                    console.error('Error uploading logo:', error);
                    throw error;
                } finally {
                    setLogoUploading(false);
                }
            })();
            uploadPromises.push({ type: 'logoUrl', promise: logoPromise });
        }

        if (coverFile) {
            setCoverUploading(true);
            const coverPromise = (async () => {
                try {
                    return await imageService.uploadSingle(coverFile, restaurantId, 'cover');
                } catch (error) {
                    console.error('Error uploading cover:', error);
                    throw error;
                } finally {
                    setCoverUploading(false);
                }
            })();
            uploadPromises.push({ type: 'coverUrl', promise: coverPromise });
        }

        const results = {};
        for (const { type, promise } of uploadPromises) {
            try {
                results[type] = await promise;
            } catch (error) {
                console.error(`Failed to upload ${type}:`, error);
            }
        }

        return results;
    },

    /**
     * Handles image upload with validation and preview creation
     */
    handleUpload: async (event, setFile, setPreview, maxSizeMB = 5) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            imageService.validateFile(file, maxSizeMB);
            setFile(file);
            const preview = await imageService.createPreview(file);
            setPreview(preview);
        } catch (error) {
            alert(error.message);
            // Clear the file input
            event.target.value = '';
        }
    },

    /**
     * Removes image file and preview
     */
    remove: (setFile, setPreview, inputId) => {
        setFile(null);
        setPreview(null);
        // Clear the file input
        const input = document.getElementById(inputId);
        if (input) input.value = '';
    }
};